import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Project {
  id: string;
  project_name: string;
  project_description: string;
  first_name: string;
  last_name: string;
  rating: number;
  created_at: string;
  user_id: string;
}

export default function AllProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'rating' | 'date'>('rating');

  useEffect(() => {
    fetchProjectsAndUser();
  }, [sortBy]);

  const fetchProjectsAndUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        setCurrentUserId(session.user.id);
      }

      let query = supabase
        .from('registrations')
        .select('id, project_name, project_description, first_name, last_name, rating, created_at, user_id')
        .eq('payment_status', 'completed');

      if (sortBy === 'rating') {
        query = query.order('rating', { ascending: false }).order('created_at', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erreur lors du chargement des projets:', error);
        return;
      }

      if (data) {
        setProjects(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRankBadge = (index: number) => {
    if (sortBy !== 'rating') return null;
    
    if (index === 0) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg">
          <i className="ri-trophy-line text-white"></i>
          <span className="text-white font-bold text-sm">1er</span>
        </div>
      );
    } else if (index === 1) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-gray-300 to-gray-500 rounded-lg">
          <i className="ri-medal-line text-white"></i>
          <span className="text-white font-bold text-sm">2ème</span>
        </div>
      );
    } else if (index === 2) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg">
          <i className="ri-award-line text-white"></i>
          <span className="text-white font-bold text-sm">3ème</span>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        <Header />
        <div className="pt-32 pb-16 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto text-center">
            <i className="ri-loader-4-line text-5xl text-secondary animate-spin"></i>
            <p className="text-gray-600 mt-4">Chargement des projets...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Header />

      <div className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  Tous les projets
                </h1>
                <p className="text-gray-600">
                  {projects.length} projet{projects.length > 1 ? 's' : ''} en compétition
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Trier par :</span>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm ${
                    sortBy === 'rating'
                      ? 'bg-gradient-to-r from-secondary to-primary text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-star-line mr-2"></i>
                  Note
                </button>
                <button
                  onClick={() => setSortBy('date')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm ${
                    sortBy === 'date'
                      ? 'bg-gradient-to-r from-secondary to-primary text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Date
                </button>
              </div>
            </div>

            {currentUserId && (
              <button
                onClick={() => navigate('/projets')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm font-semibold"
              >
                <i className="ri-user-line"></i>
                Voir mon projet
              </button>
            )}
          </motion.div>

          {projects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-8 text-center"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-folder-open-line text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Aucun projet pour le moment
              </h3>
              <p className="text-gray-600">
                Les projets participants apparaîtront ici une fois soumis.
              </p>
            </motion.div>
          ) : (
            <div className="grid gap-4 sm:gap-6">
              {projects.map((project, index) => {
                const isCurrentUser = project.user_id === currentUserId;
                const rankBadge = getRankBadge(index);

                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-4 sm:p-6 ${
                      isCurrentUser ? 'ring-2 ring-accent' : ''
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Icône et rang */}
                      <div className="flex items-center gap-3 sm:flex-col sm:items-center">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-secondary to-primary rounded-xl flex-shrink-0">
                          <i className="ri-lightbulb-flash-line text-xl sm:text-2xl text-white"></i>
                        </div>
                        {rankBadge && (
                          <div className="sm:mt-2">
                            {rankBadge}
                          </div>
                        )}
                      </div>

                      {/* Contenu du projet */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start gap-2 mb-3">
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 break-words">
                            {project.project_name}
                          </h3>
                          {isCurrentUser && (
                            <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-semibold rounded-lg whitespace-nowrap">
                              <i className="ri-user-line mr-1"></i>
                              Votre projet
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-gray-600 mb-3">
                          Par {project.first_name} {project.last_name}
                        </p>

                        <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 line-clamp-3">
                          {project.project_description}
                        </p>

                        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                          {project.rating > 0 && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                              <i className="ri-star-fill text-yellow-500"></i>
                              <span className="font-bold text-gray-900">
                                {project.rating}<span className="text-sm text-gray-600">/100</span>
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <i className="ri-calendar-line"></i>
                            <span>
                              {new Date(project.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center"
          >
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
            >
              <i className="ri-arrow-left-line"></i>
              Retour à l'accueil
            </button>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
